import { useCallback, type ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  WorkspaceNavigationContext,
  type GoToWorkspaceSection,
} from '@/modules/workspaces/workspace-navigation';

/** Bind the workspace navigation contract to the current React Router state. */
export function WorkspaceNavigationAdapter({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const currentWorkspaceSlug = /^\/w\/([^/]+)/.exec(location.pathname)?.[1];

  const goToSection = useCallback<GoToWorkspaceSection>((section, params, opts) => {
    const slug = opts?.workspaceSlug ?? currentWorkspaceSlug;
    const qs = new URLSearchParams();

    // Scope, not decoration — it survives a section change unless overridden.
    const team = searchParams.get('team');
    // Only within the same workspace: another workspace's teams are not ours.
    if (team && !opts?.workspaceSlug) qs.set('team', team);

    for (const [key, value] of Object.entries(params ?? {})) {
      if (value === null) qs.delete(key);
      else if (value !== undefined) qs.set(key, String(value));
    }

    const query = qs.toString();
    // `home` is addressed as /w/:slug, so one workspace never has two URLs.
    const path = section === 'home' ? `/w/${slug}` : `/w/${slug}/${section}`;
    navigate(query ? `${path}?${query}` : path, { replace: opts?.replace });
  }, [currentWorkspaceSlug, navigate, searchParams]);

  return (
    <WorkspaceNavigationContext.Provider value={goToSection}>
      {children}
    </WorkspaceNavigationContext.Provider>
  );
}
